import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type LfsCardContent,
} from "./types";

interface LfsCardProps {
  format: CardFormat;
  content: LfsCardContent;
}

const LfsCard = forwardRef<HTMLDivElement, LfsCardProps>(function LfsCard(
  { format, content },
  ref,
) {
  const isStory = format === "story";
  const style = content.style ?? {};
  const theme = resolveCardTheme(content.theme ?? "signature", {
    monoColor: content.monoColor,
  });

  // Resolved styles
  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 24,
        color: theme.accentColor,
        letterSpacing: "0.4em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 56 : 32,
  };

  const numberDefaultSize = isStory ? 200 : 156;
  const numbersResolved = resolveTextStyle(
    style.numbers,
    {
      fontFamily: theme.fontHeadline,
      fontSizePx: numberDefaultSize,
      color: theme.headlineColor,
      letterSpacing: "-0.04em",
      bold: true,
    },
    format,
  );

  const labelResolved = resolveTextStyle(
    style.body,
    {
      fontFamily: theme.fontBody,
      fontSizePx: 26,
      color: theme.subtleTextColor,
      letterSpacing: "0.3em",
      bold: true,
    },
    format,
  );

  const sectionLabelSize = isStory ? 18 : 16;
  const pickFlagW = isStory ? 64 : 52;
  const pickFlagH = isStory ? 44 : 36;
  const pickNameResolved = resolveTextStyle(
    style.body,
    {
      fontFamily: theme.fontBody,
      fontSizePx: 28,
      color: theme.headlineColor,
      bold: true,
    },
    format,
  );
  // Pick counts share style.body — they're row data, not hero numbers
  const pickCountResolved = resolveTextStyle(
    style.body,
    {
      fontFamily: theme.fontBody,
      fontSizePx: 30,
      color: theme.accentColor,
      bold: true,
    },
    format,
  );

  const statBlockStyle = (): CSSProperties => ({
    flex: 1,
    background: theme.headerOnLight
      ? "rgba(0,0,0,0.05)"
      : "rgba(255,255,255,0.08)",
    border: theme.headerOnLight
      ? "1px solid rgba(0,0,0,0.1)"
      : "1px solid rgba(255,255,255,0.15)",
    borderRadius: isStory ? 28 : 22,
    padding: isStory ? 36 : 28,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  });

  // Defensive: tolerate missing/non-array topPicks from corrupted state
  const topPicks = Array.isArray(content.topPicks)
    ? content.topPicks.slice(0, 3)
    : [];

  return (
    <FbkCardShell
      ref={ref}
      format={format}
      footerLeft={content.footerLeft || defaultFooterLeft("LFS_RESULT")}
      footerRight={content.hashtags || "#WC2026 · #FBK"}
      header={content.header}
      background={style.background}
      customBaseGradient={theme.customBaseGradient}
      headerOnLight={theme.headerOnLight}
      suppressBrandOverlays={theme.suppressBrandOverlays}
      hideFooter={content.footer?.hide}
    >
      <ThemeOverlay theme={theme} format={format} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={eyebrowStyle}>
          LAST FAN STANDING · ROUND {content.round}
        </div>

        {/* Twin stat hero — gold survivors, red eliminated (brand-locked colors,
            user can still override size/font/etc) */}
        <div
          style={{
            display: "flex",
            gap: isStory ? 28 : 20,
            marginTop: isStory ? 48 : 32,
          }}
        >
          <div style={statBlockStyle()}>
            <div
              style={{
                ...numbersResolved,
                color: theme.accentColor,
                lineHeight: 0.9,
              }}
            >
              {content.survivors}
            </div>
            <div
              style={{
                ...labelResolved,
                textTransform: "uppercase",
                marginTop: isStory ? 16 : 12,
              }}
            >
              Survivors
            </div>
          </div>
          <div style={statBlockStyle()}>
            <div
              style={{
                ...numbersResolved,
                color: "#FF6B6B",
                lineHeight: 0.9,
              }}
            >
              {content.eliminated}
            </div>
            <div
              style={{
                ...labelResolved,
                textTransform: "uppercase",
                marginTop: isStory ? 16 : 12,
              }}
            >
              Eliminated
            </div>
          </div>
        </div>

        {/* Top picks */}
        {topPicks.length > 0 && (
          <div style={{ marginTop: isStory ? 56 : 36 }}>
            <div
              style={{
                fontSize: sectionLabelSize,
                fontWeight: 800,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: theme.accentColor,
                marginBottom: isStory ? 24 : 16,
              }}
            >
              Most picked
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isStory ? 16 : 10,
              }}
            >
              {topPicks.map((pick, idx) => (
                <div
                  key={pick.countryIso2 + idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: isStory ? 24 : 18,
                    padding: isStory ? "18px 28px" : "14px 22px",
                    background: theme.headerOnLight
                      ? "rgba(0,0,0,0.05)"
                      : "rgba(255,255,255,0.08)",
                    border: theme.headerOnLight
                      ? "1px solid rgba(0,0,0,0.1)"
                      : "1px solid rgba(255,255,255,0.15)",
                    borderRadius: isStory ? 20 : 14,
                  }}
                >
                  {pick.countryIso2 && (
                    <img
                      src={`https://flagcdn.com/w160/${pick.countryIso2.toLowerCase()}.png`}
                      alt=""
                      crossOrigin="anonymous"
                      style={{
                        width: pickFlagW,
                        height: pickFlagH,
                        objectFit: "cover",
                        borderRadius: 6,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, ...pickNameResolved }}>
                    {pick.countryName}
                  </div>
                  <div style={pickCountResolved}>
                    {pick.count}
                    <span
                      style={{
                        fontSize: sectionLabelSize,
                        fontWeight: 700,
                        color: theme.subtleTextColor,
                        marginLeft: 6,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      picks
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </FbkCardShell>
  );
});

export default LfsCard;
